import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  TablePagination,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Add, Close } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface Role {
  externalId: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  permissions: Array<{
    externalId: string;
    action: string;
    resource: {
      externalId: string;
      name: string;
    };
  }>;
}

interface Resource {
  externalId: string;
  name: string;
  roles: Array<{
    externalId: string;
    action: string;
  }>;
}

const RolesPage: React.FC = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  const [selectedEditResources, setSelectedEditResources] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [newRole, setNewRole] = useState<
    Omit<Role, 'externalId' | 'createdAt' | 'updatedAt' | 'permissions'>
  >({
    name: '',
    description: '',
  });

  const [selectedMenuItems, setSelectedMenuItems] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch roles and resources from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [rolesResponse, resourcesResponse] = await Promise.all([
          apiService.getRoles(page + 1, rowsPerPage),
          apiService.getResources(),
        ]);
        setRoles(rolesResponse.roles);
        setTotal(rolesResponse.total);
        setResources(resourcesResponse.resources);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch data:', err);
        setError(err.message || t('roles.messages.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage]);

  const handleEdit = (externalId: string) => {
    const roleToEdit = roles.find(r => r.externalId === externalId);
    if (roleToEdit) {
      setEditingRole(roleToEdit);
      // Set selected resources based on current permissions
      // Use permission externalId (which corresponds to role.externalId from resources)
      setSelectedEditResources(roleToEdit.permissions.map(p => p.externalId));
      setIsEditModalOpen(true);
    }
  };

  const handleDelete = (externalId: string) => {
    const roleToDelete = roles.find(r => r.externalId === externalId);
    if (roleToDelete) {
      setDeletingRole(roleToDelete);
      setIsDeleteModalOpen(true);
    }
  };

  const handleAddRole = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setNewRole({
      name: '',
      description: '',
    });
    setSelectedMenuItems([]);
  };

  const handleSaveRole = async () => {
    // Validate required fields
    if (!newRole.name || !newRole.description) {
      setSnackbar({
        open: true,
        message: t('roles.messages.fillRequired'),
        severity: 'error',
      });
      return;
    }

    try {
      const roleData = {
        name: newRole.name,
        description: newRole.description,
        permissionExternalIds: selectedMenuItems,
      };
      
      console.log('Creating role with data:', roleData);
      
      const createdRole = await apiService.createRole(roleData);
      
      // Add the new role to the list
      setRoles(prev => [...prev, createdRole]);
      
      // Show success message
      setSnackbar({
        open: true,
        message: t('roles.messages.createdSuccess'),
        severity: 'success',
      });
      
      handleCloseModal();
    } catch (err: any) {
      console.error('Error creating role:', err);
      
      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('roles.messages.failedToCreate'),
        severity: 'error',
      });
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingRole(null);
    setSelectedEditResources([]);
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;

    // Validate required fields
    if (!editingRole.name || !editingRole.description) {
      setSnackbar({
        open: true,
        message: t('roles.messages.fillRequired'),
        severity: 'error',
      });
      return;
    }

    try {
      const roleData = {
        name: editingRole.name,
        description: editingRole.description,
        permissionExternalIds: selectedEditResources,
      };
      
      console.log('Updating role with data:', roleData);
      
      const updatedRole = await apiService.updateRole(editingRole.externalId, roleData);
      
      // Update the role in the list
      setRoles(prev => prev.map(r => r.externalId === editingRole.externalId ? updatedRole : r));
      
      // Show success message
      setSnackbar({
        open: true,
        message: t('roles.messages.updatedSuccess'),
        severity: 'success',
      });
      
      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating role:', err);
      
      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('roles.messages.failedToUpdate'),
        severity: 'error',
      });
    }
  };

  const handleEditInputChange = (field: keyof Role) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (editingRole) {
      setEditingRole(prev => prev ? {
        ...prev,
        [field]: event.target.value,
      } : null);
    }
  };

  const handleEditResourceChange =
    (roleExternalId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedEditResources(prev => [...prev, roleExternalId]);
      } else {
        setSelectedEditResources(prev => prev.filter(id => id !== roleExternalId));
      }
    };

  const handleInputChange =
    (field: keyof typeof newRole) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewRole(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingRole(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingRole) return;

    try {
      await apiService.deleteRole(deletingRole.externalId);
      
      // Remove the role from the list
      setRoles(prev => prev.filter(r => r.externalId !== deletingRole.externalId));
      
      // Show success message
      setSnackbar({
        open: true,
        message: t('roles.messages.deletedSuccess'),
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Error deleting role:', err);
      
      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('roles.messages.failedToDelete'),
        severity: 'error',
      });
    } finally {
      // Always close modal
      handleCloseDeleteModal();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleResourceChange =
    (roleExternalId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedMenuItems(prev => [...prev, roleExternalId]);
      } else {
        setSelectedMenuItems(prev => prev.filter(item => item !== roleExternalId));
      }
    };

  // Pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('roles.title')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Loading and Error States */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <>
              {/* Search and Add Role Controls */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  alignItems: 'center',
                  mb: 3,
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddRole}
                  sx={{ minWidth: '140px' }}
                >
                  {t('roles.addRole')}
                </Button>
              </Box>

              {/* Table */}
              <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="h6">{t('roles.table.name')}</Typography></TableCell>
                      <TableCell><Typography variant="h6">{t('roles.table.description')}</Typography></TableCell>
                      <TableCell><Typography variant="h6">{t('roles.table.createdAt')}</Typography></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.map(role => (
                      <TableRow key={role.externalId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {role.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {role.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(role.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(role.externalId)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(role.externalId)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Role Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('roles.modals.add.title')}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('roles.modals.add.roleName')}</InputLabel>
              <OutlinedInput
                value={newRole.name}
                onChange={handleInputChange('name')}
                label={t('roles.modals.add.roleName')}
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('roles.modals.add.description')}</InputLabel>
              <OutlinedInput
                multiline
                rows={3}
                value={newRole.description}
                onChange={handleInputChange('description')}
                label={t('roles.modals.add.description')}
              />
            </FormControl>

            {/* Menu Access Section */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                {t('roles.modals.add.resources')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 1,
                }}
              >
                {resources.map(resource => {
                  // Use the first role's externalId (or handle multiple roles if needed)
                  const roleExternalId = resource.roles[0]?.externalId;
                  if (!roleExternalId) return null;
                  
                  return (
                    <FormControlLabel
                      key={resource.externalId}
                      control={
                        <Checkbox
                          checked={selectedMenuItems.includes(roleExternalId)}
                          onChange={handleResourceChange(roleExternalId)}
                          color="primary"
                        />
                      }
                      label={resource.name}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} color="secondary">
            {t('roles.modals.add.cancel')}
          </Button>
          <Button onClick={handleSaveRole} variant="contained" color="primary">
            {t('roles.modals.add.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Modal */}
      {editingRole && (
        <Dialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">{t('roles.modals.edit.title')}</Typography>
              <IconButton onClick={handleCloseEditModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('roles.modals.add.roleName')}</InputLabel>
                <OutlinedInput
                  value={editingRole.name}
                  onChange={handleEditInputChange('name')}
                  label={t('roles.modals.add.roleName')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('roles.modals.add.description')}</InputLabel>
                <OutlinedInput
                  multiline
                  rows={3}
                  value={editingRole.description}
                  onChange={handleEditInputChange('description')}
                  label={t('roles.modals.add.description')}
                />
              </FormControl>

              {/* Resources Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                  {t('roles.modals.add.resources')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)',
                    },
                    gap: 1,
                  }}
                >
                  {resources.map(resource => {
                    // Use the first role's externalId (or handle multiple roles if needed)
                    const roleExternalId = resource.roles[0]?.externalId;
                    if (!roleExternalId) return null;
                    
                    return (
                      <FormControlLabel
                        key={resource.externalId}
                        control={
                          <Checkbox
                            checked={selectedEditResources.includes(roleExternalId)}
                            onChange={handleEditResourceChange(roleExternalId)}
                            color="primary"
                          />
                        }
                        label={resource.name}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseEditModal} color="secondary">
              {t('roles.modals.add.cancel')}
            </Button>
            <Button onClick={handleUpdateRole} variant="contained" color="primary">
              {t('roles.modals.edit.update')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deletingRole && (
        <Dialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{t('roles.modals.delete.title')}</Typography>
              <IconButton onClick={handleCloseDeleteModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Typography>
              {t('roles.modals.delete.confirm')} <strong>{deletingRole.name}</strong>?
              {t('roles.modals.delete.warning')}
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDeleteModal} color="secondary">
              {t('roles.modals.add.cancel')}
            </Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              {t('roles.modals.delete.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesPage;
